import * as React from 'react'
import { Loader2, Upload } from 'lucide-react'
import {
  ALLOWED_DOCUMENT_CONTENT_TYPES,
  formatBytes,
} from '@lib/constants/documents'
import type { DocumentDto } from '@lib/dto/document.dto'
import { useUploadDocument } from '@/hooks/useDocumentMutations'
import { toErrorMessage } from '@/helpers/logger'
import { Button } from '@/components/ui/button'
import type { UploadFileMeta } from '@/types/DocumentService.types'

interface UploadButtonProps {
  /** What the uploaded file is, and what it attaches to. */
  meta: UploadFileMeta
  label?: string
  /**
   * A narrower set of content types than a document's, when the caller wants
   * one — a logo, for instance, which must be something a browser can render.
   */
  accept?: readonly string[]
  /**
   * A tighter size cap than the API's, checked before anything is uploaded.
   *
   * The API is still the authority; this exists so somebody who picks a
   * 30 MB print asset is told immediately rather than after waiting for it to
   * travel.
   */
  maxBytes?: number
  onUploaded?: (document: DocumentDto) => void
  /** True from the moment a file is accepted until the upload settles. */
  onUploadingChange?: (uploading: boolean) => void
  /** Held while another save of the same record is still in flight. */
  disabled?: boolean
}

/**
 * A file picker that runs the whole upload for you.
 *
 * It wraps a hidden `<input type="file">` rather than styling the input
 * directly, because a native file input cannot be made to match the design
 * system — and a `<label>`-wrapped input loses keyboard focus styling. The
 * button is the real control; the input is plumbing.
 *
 * `accept` is driven off the shared allow-list, so the file dialog offers
 * exactly what the API will accept instead of letting someone pick a file that
 * is rejected a moment later.
 */
export function UploadButton({
  meta,
  label = 'Upload',
  accept = ALLOWED_DOCUMENT_CONTENT_TYPES,
  maxBytes,
  onUploaded,
  onUploadingChange,
  disabled = false,
}: UploadButtonProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const uploadDocument = useUploadDocument()
  const [error, setError] = React.useState<string | null>(null)

  async function handleChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const file = event.target.files?.[0]
    // Reset immediately so picking the same file twice still fires a change.
    event.target.value = ''
    if (!file) {
      return
    }

    setError(null)
    if (maxBytes !== undefined && file.size > maxBytes) {
      setError(
        `That file is ${formatBytes(file.size)}. The limit is ${formatBytes(maxBytes)}.`,
      )
      return
    }
    onUploadingChange?.(true)
    try {
      const uploaded = await uploadDocument.mutateAsync({ file, meta })
      onUploaded?.(uploaded)
    } catch (caught) {
      setError(toErrorMessage(caught))
    } finally {
      onUploadingChange?.(false)
    }
  }

  return (
    <div className="inline-flex flex-col items-start gap-1">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept.join(',')}
        onChange={(event) => {
          void handleChange(event)
        }}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled || uploadDocument.isPending}
        onClick={() => inputRef.current?.click()}
      >
        {uploadDocument.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Upload className="h-3.5 w-3.5" />
        )}
        {uploadDocument.isPending ? 'Uploading…' : label}
      </Button>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  )
}
